import { useState, useEffect } from 'react';
import errorCopy from "utils/error-copy";
import { Sparkle, PlusIcon, NotebookPen, Star, ChevronDown, ChevronUp, Pen, Trash2, ArrowRight, SquarePen, Download, Settings2, Save, Info, Pencil } from 'lucide-react';
import { getAllTopicsByTemplateId } from "api/topic";
import { getAllCustomTemplates, getAllTemplates, getFullTemplateDetails, saveTemplate, updateTemplate } from 'api/templates';
import { getUserByCogId } from 'api/user';
import { ScaleLoader } from 'react-spinners';
import { ReactComponent as StarsIcon } from '../assets/svgs/stars.svg';
import ReactDOM from "react-dom";
import { useFieldArray, useForm } from 'react-hook-form';
import InputFieldLabel from './input-field-label';
import { InputDropdown } from './InputDropdown';
import { toast } from 'react-toastify';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import Modal from './modal';
import SaveConfirmation from 'sections/modal/SaveConfirmation';
import { createFavoriteTemplate, deleteFavoriteTemplate } from 'api/favorite';
import CloseButton from 'components/ui/close-button';
import Spinner from './Spinner';
import DeleteConfirmation from 'sections/modal/DeleteConfirmation';
import UpdateConfirmation from 'sections/modal/UpdateConfirmation';
import { createFile, deleteFile, downloadFile, getFiles } from 'api/file';
import ScaleBarrierLoader from './scale-barrier-loader';
import DeleteFileConfirmation from 'sections/modal/DeleteFileConfirmation';
import UseExistingTemplateModal from './useExistingTemplateModal';
import { DndContext, closestCenter, PointerSensor, useSensor, useSensors } from "@dnd-kit/core";
import { SortableContext, arrayMove, verticalListSortingStrategy } from "@dnd-kit/sortable";
import SortableTopicRow from './SortableTopicRow';

const TemplatesSidebar = () => {

  const { control, register, reset, trigger, handleSubmit, getValues, setValue, formState: { errors } }
    = useForm({
      defaultValues: {
        templateName: "",
        description: "",
        rules: "",
        topics: [{
          summarizationType: 'Points'
        }]
      }
    });

  const inputValues = ["Paragraph", "Points"];

  const { fields, append, remove } = useFieldArray({
    control,
    name: "topics"
  });

  const [user, setUser] = useState(null);
  const [allTemplates, setAllTemplates] = useState([]);
  const [previewTemplate, setPreviewTemplate] = useState(null);
  const [previewTopics, setPreviewTopics] = useState([]);
  const [isPreviewLoading, setIsPreviewLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('all');
  const [showCustomTemplate, setShowCustomTemplate] = useState(false);
  const [noOfTopics, setNoOfTopics] = useState(1);
  const [openDropdownIndex, setOpenDropdownIndex] = useState(null);
  const [loadingAllTemplates, setLoadingAllTemplates] = useState(false);
  const [notShowTemplateDescription, setNotShowTemplateDescription] = useState([]);
  const [loadingFavoriteId, setLoadingFavoriteId] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(null);
  const [isUpdating, setIsUpdating] = useState(false);
  const [reUploadText, setReuploadText] = useState(null);
  const [showDeleteFileModal, setShowDeleteFileModal] = useState(null);
  const [showUseTemplateModal, setShowUseTemplateModal] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [showDetails, setShowDetails] = useState(false);
  const [expandAllTopics, setExpandAllTopics] = useState(false);

  // Reset the expand/collapse state whenever the create/edit modal closes.
  useEffect(() => {
    if (!showCustomTemplate) setExpandAllTopics(false);
  }, [showCustomTemplate]);

  // Initialize data
  useEffect(() => {
    const fetchData = async () => {
      setLoadingAllTemplates(true);
      try {
        const mockUser = await getUserByCogId();
        const mockTemplates = await getAllTemplates();

        setUser(mockUser);
        setAllTemplates(mockTemplates);
      } catch (error) {
        console.error("Error fetching topics:", error);
      } finally {
        setLoadingAllTemplates(false);
      }
    };

    fetchData();
  }, []);

  const handleRemoveTopic = (ind) => {
    if (fields.length === 1) {
      toast.error("At least one topic required");
      return;
    }

    remove(ind);
    setNoOfTopics(prev => prev - 1);
  };

  const handleAddTopics = () => {
    if (fields.length >= 10) {
      toast.error("You cannot add more than 10 topics");
      return;
    }

    append({
      name: "",
      summarizationType: "Points",
      prompt: ""
    });
  };

  const sortByFavorite = (templates) => {
    return [...templates].sort((a, b) => {
      if (a.isFavorite === b.isFavorite) return 0;
      return a.isFavorite ? -1 : 1;
    });
  };

  const handlePreviewClick = async (template) => {
    setPreviewTemplate(template);
    setPreviewTopics(template.topics);
  };

  const handlePreviewButtonClick = async () => {
    const isValid = await trigger();

    if (!isValid) {
      return;
    }

    const { templateId, templateName, rules, description, topics } = getValues();

    const formattedTopics = topics.map((topic, ind) => ({
      name: topic.name,
      description: topic.prompt,
      id: ind,
    }))

    const template = {
      id: templateId,
      name: templateName,
      description,
      rules,
      topics: formattedTopics,
    };

    handlePreviewClick(template);
  };

  const { mutate: saveCustomTemplate, isLoading: saveTemplateLoading } = useMutation(saveTemplate, {
    onSuccess: () => {
      reset({
        templateName: "",
        description: "",
        topics: [{ summarizationType: "Points" }]
      })
      setShowCustomTemplate(false);
      toast.success("Template Created");
      queryClient.invalidateQueries('customTemplates');
    },
    onError: () => {
      setShowCustomTemplate(false);
      toast.error("Failed to create template");
    }
  })

  const { mutate: updateCustomTemplate, isLoading: updateTemplateLoading } = useMutation(
    (data) => updateTemplate(data), {
    onSuccess: () => {
      reset({
        templateName: "",
        description: "",
        topics: [{ summarizationType: "Points" }]
      })
      setShowCustomTemplate(false);
      toast.success("Template Updated");
      queryClient.invalidateQueries('customTemplates');
    },
    onError: () => {
      setShowCustomTemplate(false);
      toast.error("Template update failed.")
    }
  })

  const deepTrim = (value) => {
    if (typeof value === "string") {
      return value.trim();
    }

    if (Array.isArray(value)) {
      return value.map(item => deepTrim(item));
    }

    if (typeof value === "object" && value !== null) {
      const trimmedObj = {};
      Object.keys(value).forEach(key => {
        trimmedObj[key] = deepTrim(value[key]);
      });
      return trimmedObj;
    }

    return value;
  };

  const handleSaveTemplate = async (data) => {
    const isValid = await trigger();
    if (!isValid) {
      toast.error("Please fill all required fields before updating");
      return;
    }

    const topicNames = data.topics.map(t => t?.name?.trim().toLowerCase());
    const nameSet = new Set(topicNames);

    if (topicNames.length !== nameSet.size) {
      toast.error('Topic names must be unique');
      return;
    }

    const topicsWithIndex = data.topics.map((t, i) => ({
      ...t,
      summarizationType: t.summarizationType === "Points" ? "bullet" : t.summarizationType,
      position: i + 1
    }));

    const finalData = {
      ...data,
      topics: topicsWithIndex
    };

    const trimmedData = deepTrim(finalData);
    saveCustomTemplate(trimmedData);
  };

  const handleUpdateTemplate = async (data) => {
    const isValid = await trigger();
    if (!isValid) return;

    const topicNames = data.topics.map(t => t.name?.trim().toLowerCase());
    const nameSet = new Set(topicNames);

    if (nameSet.size !== topicNames.length) {
      toast.error('Topic names must be unique');
      return;
    }

    const topicsWithIndex = data.topics.map((t, i) => ({
      ...t,
      summarizationType: t.summarizationType === "Points" ? "bullet" : t.summarizationType,
      position: i + 1
    }));

    const finalData = {
      ...data,
      topics: topicsWithIndex
    };

    const trimmedData = deepTrim(finalData);
    updateCustomTemplate(trimmedData);
  };

  useEffect(() => {
    if (!updateTemplateLoading)
      setShowDetails(false);
  }, [updateTemplateLoading])

  useEffect(() => {
    if (!saveTemplateLoading)
      setShowDetails(false);
  }, [saveTemplateLoading])

  const getFilteredTemplates = () => {
    if (!allTemplates.length) return [];
    return allTemplates
      .filter(t => t.medicalProfession && t.medicalProfession.trim() !== '')
      .sort((a, b) =>
        a.medicalProfession.localeCompare(b.medicalProfession) ||
        a.name.localeCompare(b.name)
      );
  };

  const { data: customTemplatesData = [], isLoading: customTemplatesLoading } = useQuery(
    ['customTemplates'], getAllCustomTemplates,
  );

  const closeModal = () => {
    setShowCustomTemplate(false);
    reset({
      templateName: "",
      description: "",
      topics: [
        { summarizationType: "Points", }
      ]
    })
    setIsUpdating(false);
    setNoOfTopics(1);
    setShowDetails(false);
  }

  const sortTopicsByPosition = (topics) => {
    if (!topics) return [];
    return [...topics].sort((a, b) => (a.position) - (b.position));
  };

  const customTemplateUpdation = async (e, templateId) => {
    e.stopPropagation();

    const templateData = await getFullTemplateDetails(templateId);

    const activeTopics = (templateData.topics || []).filter(topic => !topic.isDeleted);

    setNoOfTopics(activeTopics.length)
    reset({
      templateId: templateData.id,
      templateName: templateData.name,
      description: templateData.description,
      rules: templateData.rules,
      topics: sortTopicsByPosition(activeTopics).map(topic => ({
        id: topic.id,
        name: topic.name,
        summarizationType: topic.type === "bullet" ? "Points" : topic.type,
        prompt: topic.description,
      }))
    })
    // Close the "Use Existing" chooser (if open) so the editor isn't hidden behind it.
    setShowUseTemplateModal(false);
    setShowCustomTemplate(true);
  }

  const queryClient = useQueryClient();

  const { mutateAsync: createFavTemplate } = useMutation(
    (data) => createFavoriteTemplate(data), {
    onSuccess: () => {
      toast.success('Added to favorite templates');
      // Create Notes builds its tab strip from this query; without the
      // invalidation a newly favourited template did not appear until reload.
      queryClient.invalidateQueries('getAllFavorites');
    },
    onError: () => {
      toast.error("Something went wrong");
    }
  })

  const { mutateAsync: deleteFavTemplate } = useMutation(
    (data) => deleteFavoriteTemplate(data), {
    onSuccess: () => {
      toast.success('Removed from favorite templates');
      // Create Notes builds its tab strip from this query; without the
      // invalidation a newly favourited template did not appear until reload.
      queryClient.invalidateQueries('getAllFavorites');
    },
    onError: () => {
      toast.error("Something went wrong");
    }
  })

  const handlePreview = async (template) => {
    setPreviewTemplate(template);
    setIsPreviewLoading(true);

    try {
      const topicsByTemplate = await getAllTopicsByTemplateId(template.id);
      const activeTopics = (topicsByTemplate || []).filter(topic => !topic.isDeleted);
      setPreviewTopics(sortTopicsByPosition(activeTopics));
    } catch (error) {
      console.error('Error fetching topics:', error);
      setPreviewTopics([]);
    } finally {
      setIsPreviewLoading(false);
    }
  };

  const closePreview = () => {
    setNotShowTemplateDescription([]);
    setPreviewTemplate(null);
    setPreviewTopics([]);
  };

  const toggleFavoriteCustomTemplate = async (e, template) => {
    e.stopPropagation();
    const isFavorite = template.isFavorite;
    setLoadingFavoriteId(template.id);

    try {
      if (!isFavorite)
        await createFavTemplate({ templateId: template.id })
      else
        await deleteFavTemplate({ templateId: template.id })

      queryClient.setQueriesData(['customTemplates'], (oldData = []) => {
        return oldData.map((t) => {
          if (t.id === template.id) {
            return {
              ...t,
              isFavorite: !t.isFavorite
            }
          }
          else
            return t;
        })
      })

    } catch (error) {
      toast.error(errorCopy(error.message));
      console.log(error);
    } finally {
      setLoadingFavoriteId(null);
    }
  }

  const toggleFavoriteTemplate = async (e, template) => {
    e.stopPropagation();
    const isFavorite = template.isFavorite;
    setLoadingFavoriteId(template.id);
    try {
      if (!isFavorite)
        await createFavTemplate({ templateId: template.id });
      else
        await deleteFavTemplate({ templateId: template.id });

      setAllTemplates(prev =>
        prev.map(t =>
          t.id === template.id
            ? { ...t, isFavorite: !t.isFavorite }
            : t
        )
      );

    } catch (error) {
      toast.error(errorCopy(error.message));
      console.log(error);
    } finally {
      setLoadingFavoriteId(null);
    }
  }

  const FavoriteButton = ({ template, isFavorite }) => {
    return <button
      disabled={loadingFavoriteId === template.id}
      onClick={(e) => {
        e.stopPropagation();
        template.userId === null
          ? toggleFavoriteTemplate(e, template)
          : toggleFavoriteCustomTemplate(e, template);
      }}
      className="cursor-pointer transition-transform duration-200 "
    >
      {loadingFavoriteId === template.id ? (
        <Spinner size={18} />
      ) : (
        <Star
          className="transition-all duration-200"
          size={20}
          fill={isFavorite ? 'var(--ns-warn)' : 'none'}
          stroke="currentColor"
          strokeWidth={2}
        />
      )}
    </button>
  }

  const { mutate: createFileMutation } = useMutation(createFile,
    {
      onSuccess: () => {
        toast.success("File uploaded.");
      },
      onError: () => {
        toast.error("That upload didn't work. Try again.");
      },
      onSettled: () => {
        queryClient.invalidateQueries('getUserFiles');
        setReuploadText('');
      }
    });

  const { data: myDocs } = useQuery('getUserFiles', getFiles);

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 5,
      },
    })
  );

  const handleDragEnd = (event) => {
    const { active, over } = event;

    if (!over || active.id === over.id) return;

    const oldIndex = fields.findIndex((f) => f.id === active.id);
    const newIndex = fields.findIndex((f) => f.id === over.id);

    const newArr = arrayMove(getValues("topics"), oldIndex, newIndex);
    setValue("topics", newArr);
  };

  const TemplateCard = ({ template }) => {
    const isFavorite = template.isFavorite;
    const isCustomTemplate = template.userId;

    return (
      <div className="group flex flex-col justify-between gap-3 bg-white rounded-ns border border-rule-strong p-5  hover:shadow-pop hover:border-accent-light transition-all duration-300 transform ">
        
        <div className='flex justify-between items-start'>
          <div className='flex items-center gap-2'>
            <div className="p-2 bg-shade-50 rounded-ns group- transition-colors">
              <NotebookPen className="h-5 w-5 text-accent" />
            </div>
            {isFavorite && (
              <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-warn/10 text-warn">
                <Star className="h-3 w-3 mr-1" fill="var(--ns-warn)" />
                Favourite
              </span>
            )}
          </div>

          <div className='flex gap-2'>
            {!isCustomTemplate && <FavoriteButton template={template} isFavorite={isFavorite} />}
            
            {isCustomTemplate && (
              <>
                <button
                  onClick={(e) => {
                    setIsUpdating(true);
                    customTemplateUpdation(e, template.id);
                  }}
                  className="p-2 text-muted hover:text-accent hover:bg-shade-50 rounded-ns transition-all duration-200"
                  title="Edit template"
                >
                  <Pen size={18} />
                </button>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    setShowDeleteModal({
                      templateId: template.id,
                      templateName: template.name,
                    });
                  }}
                  className="p-2 text-muted hover:text-danger hover:bg-danger/10 rounded-ns transition-all duration-200"
                  title="Delete template"
                >
                  <Trash2 size={18} />
                </button>
                <FavoriteButton template={template} isFavorite={isFavorite} />
              </>
            )}
          </div>
        </div>

        <div className="flex-1">
          <h3 className="font-semibold text-ink text-base mb-2 line-clamp-2 group-hover:text-accent transition-colors">
            {template.name}
          </h3>
          <p className="text-muted text-sm line-clamp-3 leading-relaxed">
            {template.description}
          </p>
        </div>

        <div className="flex justify-between items-center pt-3 border-t border-rule">
          <button
            onClick={() => handlePreview(template)}
            className="inline-flex bg-shade-50 items-center gap-1.5 px-3 py-2 text-sm font-medium text-accent hover:text-accent rounded-ns transition-all duration-200 transform "
          >
            <Sparkle size={14} />
            Preview
          </button>

          {!isCustomTemplate && (
            <button
              type="button"
              onClick={(e) => customTemplateUpdation(e, template.id)}
              className="inline-flex items-center gap-1.5 px-4 py-2 text-sm font-medium text-white bg-accent rounded-ns   transition-all duration-200 transform hover:bg-deep"
            >
              Customise
              <ArrowRight size={16} />
            </button>
          )}
        </div>
      </div>
    );
  };

  const TabButton = ({ tabId, label, isActive, onClick, noRounded = false }) => (
    <button
      onClick={onClick}
      className={`px-4 py-2.5 text-sm font-medium transition-all duration-200 whitespace-nowrap transform  ${
        isActive
          ? 'bg-shade-50 text-ink '
          : 'bg-white text-muted hover:bg-shade-50 border border-rule-strong'
        }
        ${noRounded ? 'rounded-ns' : 'rounded-full'}
        `}
    >
      {label}
    </button>
  );

  const nirvaAndCustomTemplates = [...allTemplates, ...customTemplatesData];

  const filteredAllTemplates = nirvaAndCustomTemplates.filter(({ name }) =>
    name?.toLowerCase().includes(searchTerm.toLowerCase())
  ) || [];

  /* The card was a solid `bg-accent` block carrying `text-ink` headings and
     `text-muted` body — near-black and grey on dark green, both unreadable — and
     both buttons were `bg-accent` on that same accent ground, so they dissolved
     into the card and neither read as the primary action.

     It is a light card now: accent-tint is a *ground*, which is the one thing
     standards §4 allows it to be. Type goes back to ink and muted, and the two
     actions are properly ranked — accent for Create New, white secondary for Use
     Existing. */
  const CreateTemplateCard = () => (
    <div className='group flex flex-col gap-3 rounded-ns border border-dashed border-accent/30 bg-accent-tint p-5 transition-all duration-300 hover:border-accent hover:shadow-card'>

      <div className='w-fit rounded-full bg-white p-3'>
        <PlusIcon className="h-6 w-6 text-accent" strokeWidth={3} />
      </div>

      <div className='font-display text-ui-lg font-medium text-ink'>
        Create Custom Template
      </div>

      <p className='font-body text-ui-xs leading-relaxed text-muted line-clamp-3'>
        Design your own template with custom topics, formatting, and structure tailored to your practice.
      </p>

      <div className='mt-2 flex flex-wrap items-center gap-2'>
        <button
          type="button"
          onClick={() => setShowCustomTemplate(true)}
          className="inline-flex items-center gap-2 rounded-ns bg-accent px-4 py-2.5 font-body text-ui-xs font-semibold text-white transition-colors duration-ui ease-ui hover:bg-deep active:scale-[.98]"
        >
          <Pencil className='h-4 w-4'/>
          Create New
        </button>

        <button
          type="button"
          onClick={() => setShowUseTemplateModal(true)}
          className="inline-flex items-center gap-2 rounded-ns border border-solid border-rule-strong bg-white px-4 py-2.5 font-body text-ui-xs font-semibold text-ink transition-colors duration-ui ease-ui hover:border-accent hover:text-accent active:scale-[.98]"
        >
          <SquarePen size={16} />
          Use Existing
        </button>
      </div>
    </div>
  )

  const UploadFormCard = () => (
    <label htmlFor='upload-file'>
      {/* Same treatment as CreateTemplateCard — it had the identical
          unreadable-type-on-accent problem. */}
      <div className='group flex cursor-pointer flex-col gap-3 rounded-ns border border-dashed border-accent/30 bg-accent-tint p-5 transition-all duration-300 hover:border-accent hover:shadow-card'>

        <div className='w-fit rounded-full bg-white p-3'>
          <PlusIcon className="h-6 w-6 text-accent" strokeWidth={3} />
        </div>

        <div className='font-display text-ui-lg font-medium text-ink'>
          Upload Form
        </div>

        <p className='font-body text-ui-xs leading-relaxed text-muted line-clamp-3'>
          Pdf form to be filled
        </p>

        <div className="mt-2 inline-flex w-fit items-center gap-2 rounded-ns bg-accent px-4 py-2.5 font-body text-ui-xs font-semibold text-white transition-colors duration-ui ease-ui hover:bg-deep">
          <StarsIcon className='h-4 w-4' />
          Create
        </div>
      </div>
    </label>
  )

  const handleDownload = async (docId) => {
    const fileData = await downloadFile(docId);
    const link = document.createElement("a");
    link.href = fileData.url;
    link.download = fileData.fileName;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const MyDocCard = ({ doc }) => {
    return (
      <div className="group flex flex-col justify-between gap-3 bg-white rounded-ns border border-rule-strong p-5  hover:shadow-pop hover:border-warn transition-all duration-300 transform ">
        
        <div className='w-full flex justify-between items-start'>
          <div className="p-2 bg-warn/10 rounded-ns group-/10 transition-colors">
            <NotebookPen className="h-5 w-5 text-warn" />
          </div>

          <button
            onClick={(e) => {
              e.stopPropagation();
              setShowDeleteFileModal({
                docId: doc.id,
                docName: doc.fileName,
              })
            }}
            className="p-2 text-muted hover:text-danger hover:bg-danger/10 rounded-ns transition-all duration-200"
          >
            <Trash2 size={18} />
          </button>
        </div>

        <h3 className="font-semibold text-ink text-base line-clamp-2">
          {doc.fileName}
        </h3>

        <p className="text-muted text-sm line-clamp-3 leading-relaxed">
          {doc.description}
        </p>

        <div className='flex justify-end pt-2 border-t border-rule'>
          <button
            onClick={() => handleDownload(doc.id)}
            className="p-2 text-accent hover:bg-shade-50 rounded-ns transition-all duration-200"
          >
            <Download size={20} />
          </button>
        </div>
      </div>
    );
  };

  const filteredTemplates = getFilteredTemplates();

  return (
    <div className="w-full overflow-y-auto mb-4">
      
      {/* Tab Navigation */}
      <div className="mb-8 bg-white rounded-ns border border-rule-strong p-4 ">
        <div className="flex flex-col gap-4">
          <div className="flex gap-3 overflow-x-auto pb-2 scrollbar-hide">
            <TabButton
              noRounded={true}
              tabId="nirva"
              label="Nirva Templates"
              isActive={activeTab === 'all'}
              onClick={() => setActiveTab('all')}
            />
            <TabButton
              noRounded={true}
              tabId="custom"
              label="My Templates"
              isActive={activeTab === 'custom'}
              onClick={() => setActiveTab('custom')}
            />
            {/* TODO(FILL_A_DOC): Intentionally disabled. Uncomment to re-enable. */}
            {/* <TabButton
              noRounded={true}
              tabId="myDocs"
              label="My Documents"
              isActive={activeTab === 'myDocs'}
              onClick={() => setActiveTab('myDocs')}
            /> */}
          </div>
          
          <div className='flex items-center gap-2 px-3 py-2 bg-warn/10 border border-warn rounded-ns'>
            <Star fill="var(--ns-warn)" className="text-warn w-5 h-5 flex-shrink-0" strokeWidth={2} />
            <p className='text-sm text-muted'>
              <span className="font-medium">Favourite templates</span> will be auto-generated during consultation sessions
            </p>
          </div>
        </div>

        <input
          id="upload-file"
          accept={process.env.REACT_APP_ACCEPT_DOCs}
          type="file"
          className="hidden"
          onChange={(e) => {
            const file = e.target.files?.[0];
            if (!file) return;
            if (file.size > process.env.REACT_APP_MAX_FILE_SIZE_IN_MB * 1024 * 1024) {
              toast.error("File size must be under 10 MB");
              return;
            }
            setReuploadText('File Uploading...');
            createFileMutation(file);
          }}
        />
      </div>

      {/* Content */}
      <div className="space-y-6">
        {activeTab === 'custom' ? (
          customTemplatesLoading ? (
            <div className="flex items-center justify-center py-20">
              <div className="animate-spin rounded-full h-12 w-12 border-4 border-rule border-t-accent"></div>
            </div>
          ) : (
            <div data-reveal-group className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              <CreateTemplateCard />
              {sortByFavorite(customTemplatesData).map((template, index) => (
                <div key={template.id}>
                  <TemplateCard template={template} />
                </div>
              ))}
            </div>
          )
        ) : activeTab === 'myDocs' ? (
          customTemplatesLoading ? (
            <div className="flex items-center justify-center py-20">
              <div className="animate-spin rounded-full h-12 w-12 border-4 border-rule border-t-accent"></div>
            </div>
          ) : (
            <div data-reveal-group className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              <UploadFormCard />
              {myDocs?.map((doc, index) => (
                <div
                  key={doc.id}
                  className="animate-fade-in"
                  style={{ animationDelay: `${index * 50}ms` }}
                >
                  <MyDocCard doc={doc} />
                </div>
              ))}
            </div>
          )
        ) : (
          <div data-reveal-group className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {loadingAllTemplates ? (
              <div className="col-span-full flex items-center justify-center py-20">
                <div className="animate-spin rounded-full h-12 w-12 border-4 border-rule border-t-accent"></div>
              </div>
            ) : filteredTemplates.length === 0 ? (
              <div className="col-span-full text-center py-12">
                <NotebookPen className="mx-auto h-12 w-12 text-muted mb-4" />
                <p className="text-muted text-base">No templates found for this category.</p>
              </div>
            ) : (
              sortByFavorite(filteredTemplates).map((template, index) => (
                <div key={template.id}>
                  <TemplateCard template={template} />
                </div>
              ))
            )}
          </div>
        )}
      </div>

      {showUseTemplateModal && (
        <UseExistingTemplateModal
          setShowUseTemplateModal={setShowUseTemplateModal}
          filteredAllTemplates={filteredAllTemplates}
          handlePreviewClick={handlePreview}
          setSearchTerm={setSearchTerm}
          searchTerm={searchTerm}
          customTemplateUpdation={customTemplateUpdation}
        />
      )}

      {/* Preview Modal */}
      {previewTemplate && (
        <div className="fixed inset-0 bg-ink/50 backdrop-blur-sm flex items-center justify-center z-[92] p-4 animate-fade-in">
          <div className="bg-white rounded-ns max-w-3xl w-full max-h-[85vh] overflow-hidden shadow-pop animate-scale-in">
            
            {/* Modal Header.

                This was `bg-accent` carrying a `text-ink` heading, a `text-muted`
                subtitle and a `text-accent` icon — near-black and grey on dark
                green, and an icon in the ground's own colour. The icon being
                invisible is what made the template name look mysteriously
                indented: the space was the glyph nobody could see. */}
            <div className="flex items-center justify-between gap-4 border-b border-solid border-rule bg-white p-6">
              <div className="flex min-w-0 flex-1 items-center gap-3">
                <span className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-full bg-accent-tint">
                  <Sparkle className="h-4 w-4 text-accent" />
                </span>
                <div className="min-w-0">
                  <h3 className="truncate font-display text-ui-lg font-medium text-ink">
                    {previewTemplate.name}
                  </h3>
                  <p className="font-body text-ui-xs text-muted">Template preview</p>
                </div>
              </div>
              <CloseButton onClick={closePreview} label="Close preview" />
            </div>

            {/* Modal Content */}
            <div className="p-6 overflow-y-auto max-h-[60vh]">
              <div className="mb-6">
                <p className="text-ink text-sm leading-relaxed">
                  {previewTemplate.description}
                </p>

                {/*{previewTemplate.rules && previewTemplate.rules.length !== 0 && (
                  <div className="mt-6 p-4 bg-shade-50 border border-rule rounded-ns">
                    <h4 className="font-semibold text-ink text-sm mb-2 flex items-center gap-2">
                      <div className="w-1 h-4 bg-accent rounded-full"></div>
                      Template Rules
                    </h4>
                    <p className="text-ink text-sm leading-relaxed">
                      {previewTemplate.rules}
                    </p>
                  </div>
                )}*/}
              </div>

              {/* Topics */}
              <div>
                <h4 className="font-semibold text-ink text-base mb-3 flex items-center gap-2">
                  <div className="w-1 h-5 bg-accent rounded-full"></div>
                  Template Topics
                </h4>
                <p className="text-ink text-sm mb-4">
                  Consultations are categorised and summarised under these topics.
                </p>
                
                {isPreviewLoading ? (
                  <div className="flex items-center justify-center py-8">
                    <div className="animate-spin rounded-full h-8 w-8 border-4 border-rule border-t-accent"></div>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {previewTopics.map((topic, index) => (
                      <div
                        key={index}
                        className='bg-white border border-rule-strong rounded-ns hover:border-accent-light transition-all duration-200 overflow-hidden'
                      >
                        <button
                          onClick={() => setNotShowTemplateDescription(prev => {
                            let newIds = [...prev];
                            if (!newIds.includes(topic.id)) {
                              newIds.push(topic.id);
                            } else {
                              newIds = newIds.filter(newTopicId => newTopicId !== topic.id)
                            }
                            return newIds;
                          })}
                          className="w-full flex items-center justify-between p-4 hover:bg-shade-50 transition-colors"
                        >
                          <h5 className="font-medium text-ink text-sm text-left">
                            {topic.name || 'Untitled Topic'}
                          </h5>
                          {notShowTemplateDescription.includes(topic.id) ? 
                            <ChevronDown className="h-5 w-5 text-ink" /> : 
                            <ChevronUp className="h-5 w-5 text-ink" />
                          }
                        </button>

                        {!notShowTemplateDescription.includes(topic.id) && (
                          <div className='border-t border-rule p-4 bg-shade-50 text-sm text-ink leading-relaxed animate-slide-down'>
                            {topic.description}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* Modal Footer */}
            <div className="flex justify-end px-6 py-4 border-t border-rule bg-shade-50">
              <button
                onClick={closePreview}
                className="px-4 py-2 text-sm font-medium text-ink bg-danger/10 rounded-ns   transition-all duration-200 transform  hover:text-white"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Create/Edit Template Modal */}
      {showCustomTemplate && (
        <>
          {ReactDOM.createPortal(
            <div className="fixed inset-0 z-[89] flex items-center justify-center p-4 animate-fade-in">
              <div
                className="absolute inset-0 bg-ink/70 backdrop-blur-sm"
                onClick={closeModal}
              />
              <div
                className="relative w-[90vw] sm:w-[80vw] lg:w-[70vw] h-[90vh] max-w-[1000px] rounded-ns bg-white shadow-pop p-6 flex flex-col border border-rule z-[91] animate-scale-in"
              >
              <div className="relative w-full flex flex-col h-full">
                <div className="flex justify-between items-center mb-6 pb-4 border-b border-rule">
                  <h2 className="font-display text-h5 font-medium text-ink">
                    {!isUpdating ? 'Create Custom Template' : 'Update Template'}
                  </h2>
                  <CloseButton onClick={closeModal} label="Close" />
                </div>

                <div className="flex flex-col gap-4 mb-4">
                  <div className="flex flex-col">
                    <InputFieldLabel isRequired={true} label="Template Name" className="mb-1.5 text-sm font-medium" />
                    <input
                      {...register("templateName", { required: "Template name is required" })}
                      placeholder="Enter template name..."
                      className="py-2 px-4 input-field-classes text-base border border-rule-strong rounded-ns focus:ring-2 focus:ring-accent focus:border-transparent"
                    />
                    {errors?.templateName && (
                      <span className="text-danger text-sm mt-1">{errors.templateName.message}</span>
                    )}
                  </div>

                  {showDetails && (
                    <>
                      <div className="flex flex-col">
                        <InputFieldLabel label="Description" className="mb-1.5 text-sm font-medium" />
                        <textarea
                          {...register("description")}
                          className="py-2 px-4 input-field-classes border border-rule-strong rounded-ns focus:ring-2 focus:ring-accent focus:border-transparent resize-none"
                          rows={3}
                          placeholder="Describe your template..."
                        />
                      </div>

                      <div className="flex flex-col">
                        <InputFieldLabel label="Rules" className="mb-1.5 text-sm font-medium" />
                        <textarea
                          {...register("rules")}
                          className="py-2 px-4 input-field-classes border border-rule-strong rounded-ns focus:ring-2 focus:ring-accent focus:border-transparent resize-none"
                          rows={3}
                          placeholder="Add any specific rules or guidelines..."
                        />
                      </div>
                    </>
                  )}

                  <button
                    onClick={() => setShowDetails(!showDetails)}
                    className="flex items-center gap-2 text-sm text-accent hover:text-accent font-medium self-start"
                  >
                    {showDetails ? (
                      <>
                        <ChevronUp size={16} />
                        Hide Details
                      </>
                    ) : (
                      <>
                        <ChevronDown size={16} />
                        Show Details
                      </>
                    )}
                  </button>
                </div>

                <div className="flex-1 overflow-y-auto border border-rule rounded-ns p-4 bg-shade-50 space-y-4">
                  <div className="flex items-center justify-between mb-3">
                    <h3 className="font-semibold text-ink">Topics</h3>
                    <button
                      type="button"
                      onClick={() => setExpandAllTopics(prev => !prev)}
                      className="inline-flex items-center gap-1.5 text-sm font-medium text-accent hover:text-accent"
                    >
                      {expandAllTopics ? (
                        <>
                          <ChevronUp size={16} />
                          Collapse all
                        </>
                      ) : (
                        <>
                          <ChevronDown size={16} />
                          Expand all
                        </>
                      )}
                    </button>
                  </div>

                  <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
                    <SortableContext items={fields.map(f => f.id)} strategy={verticalListSortingStrategy}>
                      {fields.map((field, ind) => (
                        <SortableTopicRow
                          key={field.id}
                          field={field}
                          ind={ind}
                          register={register}
                          errors={errors}
                          getValues={getValues}
                          setValue={setValue}
                          fields={fields}
                          inputValues={inputValues}
                          handleRemoveTopic={handleRemoveTopic}
                          openDropdownIndex={openDropdownIndex}
                          setOpenDropdownIndex={setOpenDropdownIndex}
                          expanded={expandAllTopics}
                        />
                      ))}
                    </SortableContext>
                  </DndContext>

                  {fields.length < 10 && (
                    <button
                      type="button"
                      onClick={handleAddTopics}
                      className="w-full flex items-center justify-center gap-2 py-3 border-2 border-dashed border-rule-strong rounded-ns hover:border-accent-light hover:bg-shade-50 transition-all duration-200"
                    >
                      <PlusIcon className="h-5 w-5 text-muted" />
                      <span className="text-sm font-medium text-muted">Add Topic</span>
                    </button>
                  )}
                </div>

                <div className="flex justify-end gap-3 mt-6 pt-4 border-t border-rule">
                  <button
                    onClick={handlePreviewButtonClick}
                    disabled={saveTemplateLoading || updateTemplateLoading}
                    className="inline-flex items-center gap-2 px-5 py-2.5 text-sm font-medium text-ink bg-shade-50 rounded-ns   transition-all duration-200 transform "
                  >
                    <Sparkle size={16} />
                    Preview
                  </button>

                  {!isUpdating ? (
                    <button
                      onClick={handleSubmit(handleSaveTemplate)}
                      disabled={saveTemplateLoading}
                      className="inline-flex items-center gap-2 px-5 py-2.5 text-sm font-medium text-ink bg-shade-50 hover:bg-accent rounded-ns   transition-all duration-200 transform  disabled:opacity-50 disabled:cursor-not-allowed hover:text-white"
                    >
                      {saveTemplateLoading ? 'Saving...' : 'Save Template'}
                    </button>
                  ) : (
                    <button
                      className="inline-flex items-center gap-2 px-5 py-2.5 text-sm font-medium text-ink bg-shade-50 hover:bg-accent rounded-ns   transition-all duration-200 transform  disabled:opacity-50 disabled:cursor-not-allowed hover:text-white"
                      onClick={handleSubmit(handleUpdateTemplate)}
                      disabled={updateTemplateLoading}
                    >
                      {updateTemplateLoading ? 'Updating...' : 'Update Template'}
                    </button>
                  )}
                </div>
              </div>
              </div>
            </div>,
            document.body
          )}
        </>
      )}

      {showDeleteFileModal && (
        <Modal
          isOpen={showDeleteFileModal}
          closeModal={() => setShowDeleteFileModal(false)}
        >
          <DeleteFileConfirmation
            data={showDeleteFileModal}
            setShowDeleteFileModal={setShowDeleteFileModal}
          />
        </Modal>
      )}

      {showDeleteModal && (
        <Modal
          isOpen={showDeleteModal}
          closeModal={() => setShowDeleteModal(false)}
        >
          <DeleteConfirmation
            data={showDeleteModal}
            setShowDeleteModal={() => setShowDeleteModal(false)}
          />
        </Modal>
      )}

      {reUploadText && <ScaleBarrierLoader text={reUploadText} />}

      <style jsx>{`
        .scrollbar-hide::-webkit-scrollbar {
          display: none;
        }

        .scrollbar-hide {
          -ms-overflow-style: none;
          scrollbar-width: none;
        }
      `}</style>
    </div>
  );
};

export default TemplatesSidebar;